// @flow
import { DISABLE_HOLIDAY_MODE, SET_HOLIDAY_MODE } from '~/core/state/holiday-mode/constants';
import { fromJS, Map } from 'immutable';
import {
    GET_PRODUCTS,
    MODIFY_PRODUCT_STATE,
    PRODUCT_MODE_CHANGE_UPDATE_PRODUCT,
    RECEIVE_PRODUCTS,
    REMOVE_PRODUCT,
    REMOVE_PRODUCT_ZONE,
    RENAME_PRODUCT,
    RENAME_PRODUCT_ZONE,
    UNDO_PRODUCT_CHANGE
} from './constants';

import bulbReducers from './bulbs/';
import { productReducers as senseReducers } from './sense/';
import { deviceModes } from '~/core/constants/devices';
import { getProductIndexById } from './selectors';
import { handleActions } from 'redux-actions';
import handleReduce from '~/core/state/handle-reduce';
import { undoable } from './utils';

const changeProductState = handleReduce(
    undoable((state, { payload }) => {
        const currentProductIndex = getProductIndexById(state, payload.productId);

        if (currentProductIndex === -1) {
            return state;
        }

        const productItem = state.productItems.get(currentProductIndex);
        const updatedProductItem = productItem.mergeDeepIn(['state'], Map(payload.productStateChanges));

        return {
            ...state,
            productItems: state.productItems.set(currentProductIndex, updatedProductItem)
        };
    })
);

const holidayModeProds = ['heating', 'hotwater'];

export const reducers = {
    [SET_HOLIDAY_MODE]: handleReduce(state => ({
        ...state,
        productItems: state.productItems.map(pi => {
            return holidayModeProds.includes(pi.get('type')) ? pi.setIn(['props', 'holidayMode', 'enabled'], true) : pi;
        })
    })),
    [DISABLE_HOLIDAY_MODE]: handleReduce(state => ({
        ...state,
        productItems: state.productItems.map(pi => {
            return holidayModeProds.includes(pi.get('type'))
                ? pi.setIn(['props', 'holidayMode', 'enabled'], false)
                : pi;
        })
    })),
    [GET_PRODUCTS]: handleReduce(state => ({
        ...state,
        isFetching: true
    })),
    [RECEIVE_PRODUCTS]: handleReduce(
        (state, { payload: { products, loadedAt } }) => ({
            ...state,
            isFetching: false,
            isLoaded: true,
            hasError: false,
            loadedAt,
            productItems: fromJS(products)
        }),
        (state, { payload: { loadedAt } }) => ({
            ...state,
            isFetching: false,
            isLoaded: true,
            hasError: true,
            loadedAt
        })
    ),
    [PRODUCT_MODE_CHANGE_UPDATE_PRODUCT]: handleReduce(
        undoable(
            (
                state,
                { payload: { userId, mode, productId, currentSchedule, defaultTargetTemperature, cookieTarget } }
            ) => {
                const index = getProductIndexById(state, productId);
                const currentState = state.productItems.getIn([index, 'state']);

                const type = state.productItems.getIn([index, 'type']);
                let nextState = currentState.set('mode', mode);

                if (type === 'hotwater') {
                    nextState = nextState.set(
                        'status',
                        [deviceModes.MANUAL, deviceModes.BOOST].includes(mode) ? deviceModes.ON : deviceModes.OFF
                    );
                }

                if (mode === 'SCHEDULE' && currentSchedule) {
                    const scheduleValue = currentSchedule.get('value');
                    nextState = nextState.merge(scheduleValue);
                }

                if (type === 'heating') {
                    const frostProtection = currentState.get('frostProtection', 7);

                    if (mode === deviceModes.OFF) {
                        nextState = nextState.set('target', frostProtection);
                    } else if (mode === deviceModes.MANUAL) {
                        const nextTarget = cookieTarget ? parseFloat(cookieTarget) : defaultTargetTemperature;

                        nextState = nextState.set('target', nextTarget);
                        state.productItems = state.productItems.setIn(
                            [index, 'props', 'previous'],
                            fromJS({ mode: deviceModes.MANUAL, target: nextTarget })
                        );
                    }

                    if (mode !== deviceModes.MANUAL) {
                        state.productItems = state.productItems.setIn([index, 'props', 'previous'], fromJS({ mode }));
                    }
                } else if (type === 'hotwater') {
                    state.productItems = state.productItems.setIn([index, 'props', 'previous'], fromJS({ mode }));
                }

                return {
                    ...state,
                    productItems: state.productItems.setIn([index, 'state'], nextState)
                };
            }
        )
    ),
    [MODIFY_PRODUCT_STATE]: changeProductState,
    [UNDO_PRODUCT_CHANGE]: handleReduce((state, { payload }) => {
        const [index, original] = state.previous.get(payload, []);
        return {
            ...state,
            previous: state.previous.delete(payload),
            productItems: state.productItems.set(index, original)
        };
    }),
    [RENAME_PRODUCT]: handleReduce((state, { payload: { productId, name } }) => {
        const index = getProductIndexById(state, productId);
        return {
            ...state,
            productItems: state.productItems.setIn([index, 'state', 'name'], name)
        };
    }),
    [REMOVE_PRODUCT]: handleReduce((state, { payload: { id } }) => ({
        ...state,
        productItems: state.productItems.filter(p => p.id !== id && p.parent !== id)
    })),
    [RENAME_PRODUCT_ZONE]: handleReduce((state, { payload: { productId, name, zoneId } }) => {
        const updatedProductItems = state.productItems.map(
            p =>
                p.getIn(['props', 'zone']) === zoneId
                    ? p.setIn(['state', 'name'], name).setIn(['state', 'zoneName'], name)
                    : p
        );

        return {
            ...state,
            productItems: updatedProductItems
        };
    }),
    [REMOVE_PRODUCT_ZONE]: handleReduce((state, { payload: { id, zoneId } }) => {
        return {
            ...state,
            productItems: state.productItems.filter(p => p.id !== id && p.getIn(['props', 'zone']) !== zoneId)
        };
    })
};

const defaultState = {
    isFetching: false,
    hasError: false,
    isLoaded: false,
    history: fromJS({}),
    productItems: fromJS([]),
    previous: fromJS({})
};

const productReducers = {
    ...reducers,
    ...bulbReducers,
    ...senseReducers
};

export default handleActions(productReducers, defaultState);



// WEBPACK FOOTER //
// ./src/core/state/products/index.js